import React from 'react';
import { 
  CurrencyCircleDollar, House, Phone, User, MapPin, 
  CalendarDots, CheckCircle, WarningCircle, Clock, Building
} from '@phosphor-icons/react';
import './ParentHostelSection.css';

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ParentHostelSection({ data, childName }) {
  if (!data?.enrolled) {
    return (
      <div className="hostel-not-enrolled">
        <div className="not-enrolled-content">
          <House size={56} weight="thin" color="#9CA3AF" />
          <h3>Not Enrolled in Hostel</h3>
          <p>{childName} is not currently enrolled in hostel accommodation</p>
        </div>
      </div>
    );
  }

  const totalFees = data.recent_fees?.reduce((sum, fee) => sum + fee.amount_due, 0) || 0;
  const totalPaid = data.recent_fees?.reduce((sum, fee) => sum + fee.amount_paid, 0) || 0;
  const totalBalance = totalFees - totalPaid;
  const allFesPaid = data.recent_fees?.every(fee => fee.is_paid) ?? false;

  return (
    <div className="hostel-section-container">
      {/* Header */}
      <div className="hostel-section-header">
        <div className="header-content">
          <h2>Hostel Accommodation</h2>
          <p>Manage hostel information and fees for {childName}</p>
        </div>
        <div className={`header-badge ${allFesPaid ? 'paid' : 'pending'}`}>
          {allFesPaid ? (
            <>
              <CheckCircle size={20} weight="fill" />
              All Fees Paid
            </>
          ) : (
            <>
              <Clock size={20} weight="fill" />
              Pending Payment
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="hostel-stats-grid">
        <div className="stat-card room-info">
          <div className="stat-icon">
            <House size={28} weight="duotone" color="#8B4513" />
          </div>
          <div className="stat-body">
            <span className="stat-label">Hostel Room</span>
            <span className="stat-value">{data.room_number}</span>
            <span className="stat-detail">In {data.hostel_id || 'Assigned Hostel'}</span>
          </div>
        </div>

        <div className="stat-card checkin-info">
          <div className="stat-icon">
            <CalendarDots size={28} weight="duotone" color="#059669" />
          </div>
          <div className="stat-body">
            <span className="stat-label">Check-in Date</span>
            <span className="stat-value">{new Date(data.assigned_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span className="stat-detail">{data.academic_year}</span>
          </div>
        </div>

        <div className={`stat-card fee-balance ${totalBalance > 0 ? 'warning' : 'success'}`}>
          <div className="stat-icon">
            <CurrencyCircleDollar size={28} weight="duotone" color={totalBalance > 0 ? '#DC2626' : '#059669'} />
          </div>
          <div className="stat-body">
            <span className="stat-label">Fee Balance</span>
            <span className="stat-value">{formatCurrency(totalBalance)}</span>
            <span className="stat-detail">{totalBalance > 0 ? 'Outstanding' : 'Fully Paid'}</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="hostel-content-grid">
        {/* Left Column - Accommodation Details */}
        <div className="hostel-details-card">
          <div className="card-header">
            <h3>
              <Building size={22} weight="duotone" />
              Accommodation Details
            </h3>
          </div>
          <div className="card-body">
            <div className="detail-group">
              <label className="detail-label">Hostel Name</label>
              <div className="detail-value">{data.hostel_id || 'Not Specified'}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Hostel Room Number</label>
              <div className="detail-value">{data.room_number}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Academic Year</label>
              <div className="detail-value">{data.academic_year}</div>
            </div>
            <div className="detail-group">
              <label className="detail-label">Assignment Date</label>
              <div className="detail-value">{new Date(data.assigned_date).toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact Information */}
        <div className="hostel-contact-card">
          <div className="card-header">
            <h3>
              <Phone size={22} weight="duotone" />
              Contact Information
            </h3>
          </div>
          <div className="card-body">
            {data.parent_contact && (
              <div className="contact-item">
                <div className="contact-icon">
                  <User size={18} weight="duotone" />
                </div>
                <div className="contact-info">
                  <span className="contact-role">Parent Contact</span>
                  <span className="contact-value">{data.parent_contact}</span>
                </div>
              </div>
            )}
            {data.emergency_contact && (
              <div className="contact-item">
                <div className="contact-icon">
                  <User size={18} weight="duotone" color="#DC2626" />
                </div>
                <div className="contact-info">
                  <span className="contact-role">Emergency Contact</span>
                  <span className="contact-value">{data.emergency_contact}</span>
                </div>
              </div>
            )}
            {data.emergency_contact_phone && (
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={18} weight="duotone" color="#3B82F6" />
                </div>
                <div className="contact-info">
                  <span className="contact-role">Emergency Phone</span>
                  <span className="contact-value">{data.emergency_contact_phone}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fees Section */}
      <div className="hostel-fees-section">
        <div className="fees-header">
          <h3>
            <CurrencyCircleDollar size={22} weight="duotone" />
            Hostel Fee Summary
          </h3>
          <div className="fee-summary-badges">
            <div className="badge-item">
              <span className="badge-label">Total Due:</span>
              <span className="badge-value">{formatCurrency(totalFees)}</span>
            </div>
            <div className="badge-item">
              <span className="badge-label">Total Paid:</span>
              <span className="badge-value paid">{formatCurrency(totalPaid)}</span>
            </div>
            <div className="badge-item">
              <span className="badge-label">Balance:</span>
              <span className={`badge-value ${totalBalance > 0 ? 'warning' : 'success'}`}>{formatCurrency(totalBalance)}</span>
            </div>
          </div>
        </div>

        {data.recent_fees && data.recent_fees.length > 0 ? (
          <div className="fees-items">
            {data.recent_fees.map(fee => (
              <div key={fee.id} className={`fee-item ${fee.is_paid ? 'paid' : 'pending'}`}>
                <div className="fee-item-left">
                  <div className={`fee-status-dot ${fee.is_paid ? 'paid' : 'pending'}`}></div>
                  <div className="fee-item-info">
                    <span className="fee-type">{fee.fee_type}</span>
                    <span className="fee-period">
                      {fee.due_date ? `Due: ${new Date(fee.due_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Due date not specified'}
                    </span>
                  </div>
                </div>
                <div className="fee-item-amounts">
                  <div className="amount-cell">
                    <span className="amount-label">Due</span>
                    <span className="amount-value">{formatCurrency(fee.amount_due)}</span>
                  </div>
                  <div className="amount-cell">
                    <span className="amount-label">Paid</span>
                    <span className="amount-value paid">{formatCurrency(fee.amount_paid)}</span>
                  </div>
                  <div className="amount-cell">
                    <span className="amount-label">Balance</span>
                    <span className={`amount-value ${fee.balance > 0 ? 'warning' : 'success'}`}>
                      {formatCurrency(fee.balance)}
                    </span>
                  </div>
                </div>
                <div className="fee-item-status">
                  <span className={`status-badge ${fee.is_paid ? 'paid' : 'pending'}`}>
                    {fee.is_paid ? <><CheckCircle size={14} weight="fill" /> Paid</> : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-fees-state">
            <CurrencyCircleDollar size={40} weight="light" color="#D1D5DB" />
            <p>No fees recorded for hostel accommodation</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParentHostelSection;
